/**
 * Pure tournament orchestration logic — no socket/io or persistence dependency.
 * A Tournament carries no hidden information (every participant can see the
 * whole thing), so these functions operate directly on the shared `Tournament`
 * type and return new values rather than mutating in place.
 */
import type { Side, Tournament, TournamentMatch, TournamentParticipant, TournamentStanding } from './types';

export function createTournament(
  id: string, name: string, hostClientId: string, hostPlayerName: string, scenarioIds: string[],
): Tournament {
  return {
    id, name, hostClientId, scenarioIds,
    status: 'lobby',
    participants: [{ clientId: hostClientId, playerName: hostPlayerName }],
    matches: [],
    createdAt: Date.now(),
  };
}

export function addParticipant(
  t: Tournament, clientId: string, playerName: string,
): Tournament | { error: string } {
  if (t.status !== 'lobby') return { error: 'This tournament has already started' };
  if (t.participants.some(p => p.clientId === clientId)) return t; // idempotent reconnect
  return { ...t, participants: [...t.participants, { clientId, playerName }] };
}

/**
 * Round-robin schedule: every unordered pair of participants plays every
 * selected scenario twice, once with each assignment of sides, so both
 * players get a turn on each side of every scenario. Order within the
 * returned list carries no meaning — players may play matches in any order,
 * including playing the "rematch" leg long before or after the first leg.
 */
export function generateSchedule(
  participants: TournamentParticipant[], scenarioIds: string[], scenarioName: (id: string) => string,
): TournamentMatch[] {
  const matches: TournamentMatch[] = [];
  let idx = 0;
  for (let i = 0; i < participants.length; i++) {
    for (let j = i + 1; j < participants.length; j++) {
      const p1 = participants[i], p2 = participants[j];
      for (const scenarioId of scenarioIds) {
        const name = scenarioName(scenarioId);
        matches.push({
          id: `m${idx++}`, scenarioId, scenarioName: name,
          playerA: { ...p1, side: 'allies' }, playerB: { ...p2, side: 'axis' },
          status: 'pending',
        });
        matches.push({
          id: `m${idx++}`, scenarioId, scenarioName: name,
          playerA: { ...p2, side: 'allies' }, playerB: { ...p1, side: 'axis' },
          status: 'pending',
        });
      }
    }
  }
  return matches;
}

/** Total matches a schedule would produce, for the create-tournament live preview. */
export function scheduleSize(participantCount: number, scenarioCount: number): number {
  return participantCount * (participantCount - 1) * scenarioCount;
}

export function startTournament(
  t: Tournament, scenarioName: (id: string) => string,
): Tournament | { error: string } {
  if (t.status !== 'lobby') return { error: 'Tournament has already started' };
  if (t.participants.length < 2) return { error: 'Need at least 2 players to start' };
  if (t.scenarioIds.length === 0) return { error: 'Select at least 1 scenario' };
  return {
    ...t,
    status: 'active',
    matches: generateSchedule(t.participants, t.scenarioIds, scenarioName),
    startedAt: Date.now(),
  };
}

export function markMatchInProgress(t: Tournament, matchId: string, roomId: string): Tournament {
  return {
    ...t,
    matches: t.matches.map(m => m.id === matchId ? { ...m, status: 'in-progress' as const, roomId } : m),
  };
}

export function recordMatchResult(
  t: Tournament, matchId: string, winnerSide: Side, alliesMedals: number, axisMedals: number,
): Tournament {
  return {
    ...t,
    matches: t.matches.map(m => {
      if (m.id !== matchId) return m;
      const winnerClientId = m.playerA.side === winnerSide ? m.playerA.clientId : m.playerB.clientId;
      return { ...m, status: 'completed' as const, result: { winnerClientId, alliesMedals, axisMedals } };
    }),
  };
}

export function endTournamentEarly(t: Tournament): Tournament {
  if (t.status === 'completed') return t;
  return { ...t, status: 'completed', completedAt: Date.now() };
}

/** True once every match has been played — the tournament can be marked completed automatically. */
export function isFullyComplete(t: Tournament): boolean {
  return t.status === 'active' && t.matches.length > 0 && t.matches.every(m => m.status === 'completed');
}

export function computeStandings(t: Tournament): TournamentStanding[] {
  const standings = new Map<string, TournamentStanding>();
  for (const p of t.participants) {
    standings.set(p.clientId, {
      clientId: p.clientId, playerName: p.playerName,
      played: 0, wins: 0, losses: 0, medalsFor: 0, medalsAgainst: 0, points: 0,
    });
  }
  for (const m of t.matches) {
    if (m.status !== 'completed' || !m.result) continue;
    const a = standings.get(m.playerA.clientId);
    const b = standings.get(m.playerB.clientId);
    if (!a || !b) continue;
    const { winnerClientId, alliesMedals, axisMedals } = m.result;
    const aMedals = m.playerA.side === 'allies' ? alliesMedals : axisMedals;
    const bMedals = m.playerB.side === 'allies' ? alliesMedals : axisMedals;
    a.played++; b.played++;
    a.medalsFor += aMedals; a.medalsAgainst += bMedals;
    b.medalsFor += bMedals; b.medalsAgainst += aMedals;
    if (winnerClientId === m.playerA.clientId) { a.wins++; a.points++; b.losses++; }
    else if (winnerClientId === m.playerB.clientId) { b.wins++; b.points++; a.losses++; }
  }
  return [...standings.values()].sort((x, y) =>
    y.points - x.points || (y.medalsFor - y.medalsAgainst) - (x.medalsFor - x.medalsAgainst));
}
